/**
 * Extract domain from a URL
 * @param {string} url - The URL to extract domain from
 * @returns {string|null} - The domain or null if invalid
 */
export const extractDomain = (url) => {
  if (!url) return null;
  try {
    const urlObj = new URL(url);
    return urlObj.hostname.replace(/^www\./, '');
  } catch {
    return null;
  }
};

/**
 * Transform grouped tables response to database view format
 * @param {Array} groups - Array of grouped table objects from /tables/grouped API
 * @returns {Array} - Transformed sources with domain and latest table info
 */
export const transformGroupedTablesToSources = (groups) => {
  return groups
    .filter(group => group.latestTableId)
    .map(group => ({
      id: group.sourceId,
      name: group.sourceName || 'Unnamed',
      metadata: {
        url: group.sourceUrl,
        favicon: group.sourceFavicon,
      },
      domain: extractDomain(group.sourceUrl),
      taskType: group.taskType || null,
      latestTable: {
        id: group.latestTableId,
        rowCount: group.latestTableRowCount,
        createdAt: group.latestTableAt,
      },
      tableCount: group.tableCount,
      firstTableAt: group.firstTableAt,
      taskExists: group.taskExists,
    }));
};

/**
 * Convert a date range preset to ISO date strings for API params
 * @param {string} range - Preset range key
 * @returns {{ from: string|null, to: string|null }}
 */
export const dateRangeToISO = (range) => {
  const now = new Date();
  switch (range) {
    case 'week': return { from: new Date(now.getTime() - 7 * 86400000).toISOString(), to: null };
    case 'month': return { from: new Date(now.getTime() - 30 * 86400000).toISOString(), to: null };
    case '3months': return { from: new Date(now.getTime() - 90 * 86400000).toISOString(), to: null };
    default: return { from: null, to: null };
  }
};

/**
 * Format a date for display
 * @param {string|Date} dateString - Date to format
 * @param {Object} cloudApi - Cloud API instance for date conversion
 * @returns {string} - Formatted date string
 */
export const formatDate = (dateString, cloudApi) => {
  if (!dateString) return 'N/A';
  const date = cloudApi?.toLocalDate(dateString) || new Date(dateString);
  if (!date || isNaN(date.getTime())) return 'N/A';

  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

/**
 * Format a date as a relative time string (e.g., "3h ago", "2d ago")
 * @param {string|Date} dateString - Date to format
 * @returns {string} - Relative time string
 */
export const formatRelativeTime = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return '';

  const now = Date.now();
  const diffMs = now - date.getTime();
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHr = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHr / 24);
  const diffWeek = Math.floor(diffDay / 7);

  if (diffSec < 60) return 'Just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  if (diffHr < 24) return `${diffHr}h ago`;
  if (diffDay < 7) return `${diffDay}d ago`;
  if (diffWeek < 5) return `${diffWeek}w ago`;

  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

/**
 * Format a date contextually for the history timeline
 * Today → "Today, 14:30"
 * Yesterday → "Yesterday, 14:30"
 * This year → "Jan 15, 14:30"
 * Other years → "Jan 15 2024, 14:30"
 * @param {string|Date} dateString - Date to format
 * @returns {string} - Context-aware date string
 */
export const formatSmartDate = (dateString) => {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return 'N/A';

  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  const dateDay = new Date(date.getFullYear(), date.getMonth(), date.getDate());

  const time = date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false });

  if (dateDay.getTime() === today.getTime()) return `Today, ${time}`;
  if (dateDay.getTime() === yesterday.getTime()) return `Yesterday, ${time}`;

  const month = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  if (date.getFullYear() === now.getFullYear()) return `${month}, ${time}`;
  return `${month} ${date.getFullYear()}, ${time}`;
};
